import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataListTableElement, DLT_DATA_TYPES } from '@el-data-list-table';
import { TranslateService } from '@ngx-translate/core';

import { AttachmentStatus, FILE_TYPES } from '@shared/constants';
import {
  IAttachmentDocument,
  IContactEmail,
  IContactNumber,
  MultiFieldData,
} from '@shared/interfaces';
import {
  emailValidator,
  generateFileTypeRegex,
  viewDocument,
} from '@shared/utils';

export interface IBrokerCommonCustomerShareDocumentPopup {
  isFileNeeded: boolean;
  maxSize?: number;
  allowedTypesNames?: string;
  allowedTypesRegex?: RegExp;
  fileCount?: number;
  title: string;
  subtitle: string;
  icon?: string;
  contactNumber?: IContactNumber[];
  contactEmail?: IContactEmail[];
  isContactDetailsRequired?: boolean;
  subject?: string;
}

export interface IBrokerCommonCustomerShareDocumentPopupResult {
  files: File[];
  additionalEmails?: string[];
  remark: string;
  contactNumber?: IContactNumber[];
  contactMails?: IContactEmail[];
  subject?: string;
}

const ACTIONS = {
  DELETE: 'DELETE',
  VIEW_DOCUMENT: 'VIEW_DOCUMENT',
};

@Component({
  selector: 'app-common-customer-share-document',
  templateUrl: './common-customer-share-document.component.html',
  styleUrls: ['./common-customer-share-document.component.scss'],
})
export class CommonCustomerShareDocumentPopupComponent implements OnInit {
  separatorKeyCodes: number[] = [ENTER, COMMA]; // Allow Enter and Comma to separate emails

  documents: IAttachmentDocument[] = [];
  documentsDataList: DataListTableElement[] = [];
  files: File[] = [];
  isFileNeeded = false;

  uploadedFiles: string[] = [];
  imgPath: string;
  file_name = new FormControl(null, [Validators.required]);
  maxSize = 10;
  fileCount = 100;
  allowedTypesNames = '*.docx/ *.xlsx/ *.jpeg/ *.png/ *.csv and *.pdf';
  allowedTypesRegex = generateFileTypeRegex([
    FILE_TYPES.IMAGE,
    FILE_TYPES.DOCS,
    FILE_TYPES.CSV,
    FILE_TYPES.EXCEL,
    FILE_TYPES.PDF,
  ]);

  uploadForm = new FormGroup({
    subject: new FormControl<string>({ value: null, disabled: false }),
    additionalEmail: new FormControl(null, [emailValidator()]),
    remarks: new FormControl<string>({ value: null, disabled: false }),
    eMails: new FormControl<IContactEmail[]>({ value: null, disabled: false }),
    contactNumber: new FormControl<IContactNumber[]>({
      value: null,
      disabled: false,
    }),
    isMailSelected: new FormControl<boolean>({ value: false, disabled: false }),
    isContactNumberSelected: new FormControl<boolean>({
      value: false,
      disabled: false,
    }),
  });

  emailValidationError = false;
  addedEmails: string[] = [];
  isContactDetailsRequired = true;

  constructor(
    public translate: TranslateService,
    private matDialog: MatDialogRef<CommonCustomerShareDocumentPopupComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: IBrokerCommonCustomerShareDocumentPopup
  ) {}

  ngOnInit(): void {
    if (this.data.subject) {
      this.uploadForm.controls.subject.setValue(this.data.subject);
    }
    if (this.data.maxSize) {
      this.maxSize = this.data.maxSize;
    }
    if (this.data.allowedTypesNames) {
      this.allowedTypesNames = this.data.allowedTypesNames;
    }
    if (this.data.allowedTypesRegex) {
      this.allowedTypesRegex = this.data.allowedTypesRegex;
    }
    if (this.data.fileCount) {
      this.fileCount = this.data.fileCount;
    }
    if (this.data.isFileNeeded) {
      this.isFileNeeded = this.data.isFileNeeded;
    }
    if (this.data.contactEmail?.length) {
      this.uploadForm.controls.eMails.setValue(this.data.contactEmail);
    }
    if (this.data.contactNumber?.length) {
      this.uploadForm.controls.contactNumber.setValue(this.data.contactNumber);
    }
    this.isContactDetailsRequired =
      this.data.isContactDetailsRequired === false ? false : true;
    if (this.isContactDetailsRequired === false) {
      this.uploadForm.controls.additionalEmail.setValidators([
        Validators.required,
        emailValidator(),
      ]);
    }
  }

  // handling normal data fields

  onValueChange(e: MultiFieldData) {
    if (e.type === 'EMAIL') {
      this.uploadForm.controls.eMails.setValue(e.value);
    }
    if (e.type === 'PHONE') {
      this.uploadForm.controls.contactNumber.setValue(e.value);
    }
  }

  requiredCheckOnEmail(e: MatCheckboxChange) {
    this.uploadForm.controls.isMailSelected.setValue(e.checked);
    this.setValidators();
  }

  requiredCheckOnContactPhone(e: MatCheckboxChange) {
    this.uploadForm.controls.isContactNumberSelected.setValue(e.checked);
    this.setValidators();
  }

  setValidators() {
    if (this.isContactDetailsRequired) {
      if (this.uploadForm.controls.isContactNumberSelected.value) {
        this.uploadForm.controls.contactNumber.setValidators([
          Validators.required,
        ]);
      } else {
        this.uploadForm.controls.contactNumber.removeValidators([
          Validators.required,
        ]);
      }
      if (this.uploadForm.controls.isMailSelected.value) {
        this.uploadForm.controls.eMails.setValidators(Validators.required);
      } else {
        this.uploadForm.controls.eMails.removeValidators(Validators.required);
      }
      this.uploadForm.controls.eMails.updateValueAndValidity();
      this.uploadForm.controls.contactNumber.updateValueAndValidity();
    }
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  addEmail(email: string): void {
    this.emailValidationError = false; // Reset error state
    email = email.trim();

    if (email && this.isValidEmail(email)) {
      if (!this.addedEmails.includes(email)) {
        this.addedEmails.push(email); // Add unique, valid email
      }
    } else if (email) {
      this.emailValidationError = true; // Set error if invalid email
    }
  }

  removeEmail(email: string): void {
    const index = this.addedEmails.indexOf(email);
    if (index >= 0) {
      this.addedEmails.splice(index, 1);
    }
  }

  //handling document fields

  triggerFileSelected(file: File) {
    this.files.push(file);
    this.addToDocumentsList(file);
    this.fileCount = this.fileCount - 1;
  }

  private addToDocumentsList(file: File) {
    const dateFormatter = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    const timeFormatter = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    const currentDate = new Date();
    const formattedDate = dateFormatter.format(currentDate);
    const formattedTime = timeFormatter.format(currentDate);
    const fullDateTime = `${formattedDate} (${formattedTime})`;

    const documentElement: DataListTableElement = {
      icon: 'description',
      data: [
        {
          key: 'File Name',
          value: `${file.name}`,
          type: DLT_DATA_TYPES.PLAIN,
        },
        {
          key: 'Uploaded Date',
          value: `\n${fullDateTime}`,
          type: DLT_DATA_TYPES.PLAIN,
        },
      ],
      actions: [
        {
          icon: 'open_in_new',
          action: ACTIONS.VIEW_DOCUMENT,
          tooltip: 'Vew Document',
          face: '',
          data: document,
        },
        {
          icon: 'delete',
          action: ACTIONS.DELETE,
          tooltip: 'Delete',
          face: '',
          data: this.files,
        },
      ],
      loading: false,
      expanded: false,
      index: this.documentsDataList.length,
      originalData: file,
    };

    this.documentsDataList.push(documentElement);
    this.documents.push({
      file,
      status: AttachmentStatus.PENDING_UPLOAD,
      name: file.name,
      type: file.type,
      uploaded_date: new Date(),
    } as IAttachmentDocument);
  }

  action(e: { action: string; index: number }) {
    if (e.action === ACTIONS.DELETE) {
      this.deleteDocument(e.index);
    }
    if (e.action === ACTIONS.VIEW_DOCUMENT) {
      const file = this.documentsDataList[e.index].originalData as File;
      viewDocument(file as File);
    }
  }

  private deleteDocument(index: number) {
    this.documentsDataList.splice(index, 1);
    this.documents.splice(index, 1);
    this.files.splice(index, 1);
    this.fileCount = this.fileCount + 1;

    // Update indexes after deletion
    this.documentsDataList.forEach((doc, i) => {
      doc.index = i;
    });
  }

  // action btn handling

  onCancel(): void {
    this.matDialog.close();
  }

  onSave() {
    if (this.uploadForm.valid) {
      const result: IBrokerCommonCustomerShareDocumentPopupResult = {
        files: this.files,
        ...(this.uploadForm.controls.subject?.value
          ? { subject: this.uploadForm.controls.subject.value }
          : {}),
        ...(this.addedEmails ? { additionalEmails: this.addedEmails } : {}),
        remark: this.uploadForm.controls.remarks?.value,
        ...(this.uploadForm.controls.isContactNumberSelected.value
          ? { contactNumber: this.uploadForm.controls.contactNumber.value }
          : {}),
        ...(this.uploadForm.controls.isMailSelected.value
          ? { contactMails: this.uploadForm.controls.eMails.value }
          : {}),
      };

      this.matDialog.close(result);
    }
  }
}
